import {
    getBasePairs,
    searchInAlignment,
} from "./utils";

import {
    metaKey,
    rangeOverlapStart,
    alignBpPosition,
    isInsideArea,
    attributeAnnotations,
    setThreshold
} from "../editor/utils";

import {renderVirtualPopper} from "../utils/virtualPopper";

import {drag} from "d3-drag";
import {zoom as scroll} from "d3-zoom";
import {selectAll} from "d3-selection";
import {codonPath} from "../mixins/linearShape";
import {aminoSequenceColors, nomenclatures, thresholdLevels, zoomLevels} from "../mixins/dictionaries";

const d3 = {selectAll, drag, scroll}

const viewMixins = {
    addEventsMainContainer() {
        this.mainBox
            .on("contextmenu", (e) => {
                e.preventDefault();
                e.stopPropagation();
            })
            .call(d3.drag()
                .on("start drag end", e => this.eventOnCreateBrush(e))
            )
            .call(d3.scroll()
                .on("start zoom end", e => {
                    if (metaKey(e.sourceEvent, this)) {
                        e.preventDefault;
                        if (e.type === "end") {
                            const newBrushSize = {
                                x: (this.rangeBp.start - this.offsetScroll.current) * this.fontBoxWidth,
                                width: (this.rangeBp.end - this.rangeBp.start) * this.fontBoxWidth,
                            }

                            this.brush.setParameters(newBrushSize);

                            this.EventsTrigger("updateBrush", newBrushSize)
                        } else if (e.type === "zoom") {
                            this.zoom += (e.sourceEvent.wheelDelta < 0 ? .5 : -.5);
                            if (this.zoom >= zoomLevels.min && this.zoom <= zoomLevels.max) {
                                this.setFontWidth(this.zoom)
                                this.EventsTrigger("setFontWidth", this.zoom)
                            } else {
                                this.zoom = this.zoom < zoomLevels.min ? zoomLevels.min : zoomLevels.max;
                            }
                        }
                        this.selectRange(this.rangeBp)
                        this.EventsTrigger("selectRange", this.rangeBp)

                    } else {
                        if (e.sourceEvent.detail < 2 && this.zoom !== zoomLevels.max) {
                            this.scroll(e)
                        }
                    }
                })
            )
    },

    setEditorParameters() {
        if (this.zoom === zoomLevels.max) {
            this.fontBoxWidth = this.$containerWidth / this.sequenceLength;
            this.maxBasePairsInAreaView = this.sequenceLength;

            this.threshold = thresholdLevels.full;
            this.middleArea = this.sequenceLength / 2;
        } else {
            this.fontBoxWidth = (this.regularSizeFont.width / this.zoom);
            this.maxBasePairsInAreaView = Math.floor(this.$containerWidth / this.fontBoxWidth);

            this.threshold = setThreshold(this.zoom, (this.sequenceLength * this.fontBoxWidth), this.$mainBoxContainer.width);
            this.middleArea = this.offsetScroll.current + Math.round(this.maxBasePairsInAreaView / 2);
        }
    },

    getEdges(maxBasePairsInAreaView, sequenceLength) {
        let leftEdge = this.offsetScroll.current;
        let rightEdge = leftEdge + maxBasePairsInAreaView;

        if (leftEdge < 0 || rightEdge > sequenceLength) {
            if (leftEdge < 0) {
                this.offsetScroll.current = 0;
                leftEdge = this.offsetScroll.current;
                rightEdge = leftEdge + maxBasePairsInAreaView;
            } else {
                this.offsetScroll.current = sequenceLength - maxBasePairsInAreaView;
                leftEdge = this.offsetScroll.current;
                rightEdge = sequenceLength;
            }
        }

        this.edges = {
            left: leftEdge,
            right: rightEdge,
        }
    },

    drawBorders(edges) {
        if (this.zoom === zoomLevels.max && this.sequenceLength !== this.referenceLength) {
            this.mainBox.selectAll(".wrap-start").remove();
            edges.some(edge => {
                this.mainBox
                    .append("line")
                    .attr("class", "wrap-start")
                    .attr("x1", (edge - this.offsetScroll.current) * this.fontBoxWidth)
                    .attr("y1", 0)
                    .attr("x2", (edge - this.offsetScroll.current) * this.fontBoxWidth)
                    .attr("y2", "100%")
                    .style("stroke-dasharray", ("3, 3"))

            })
        } else {
            let removeEdge = true;
            edges.forEach(edge => {
                if (isInsideArea(edge, edge, this.edges.left, this.edges.right, this.alignmentLength)) {
                    const wrapEdgeNode = this.mainBox.select(".wrap-start").node();
                    const x = (edge - this.offsetScroll.current) * this.fontBoxWidth;

                    if (wrapEdgeNode) {
                        wrapEdgeNode
                            .setAttribute("x1", x)

                        wrapEdgeNode
                            .setAttribute("x2", x)

                    } else {
                        this.mainBox
                            .append("line")
                            .attr("class", "wrap-start")
                            .attr("x1", x)
                            .attr("y1", 0)
                            .attr("x2", x)
                            .attr("y2", "100%")
                            .style("stroke-dasharray", ("3, 3"))
                    }
                    removeEdge = false;
                }
            })
            if (removeEdge) {
                this.mainBox.selectAll(".wrap-start").remove();
            }
        }
    },

    setBrushSize
        () {
        const newBrushSize = {
            x: (this.rangeBp.start - this.offsetScroll.current) * this.fontBoxWidth,
            width: (this.rangeBp.end - this.rangeBp.start) * this.fontBoxWidth,
        }

        this.brush.setParameters(newBrushSize);
        return newBrushSize;
    }
    ,

    alignRangeBp(brushSize) {
        let startBpSelection = Math.round(brushSize.x / this.fontBoxWidth);
        let endBpSelection = startBpSelection + Math.round(brushSize.width / this.fontBoxWidth);

        this.rangeBp = {
            start: Math.abs(alignBpPosition(this.offsetScroll.current, startBpSelection)),
            end: Math.abs(alignBpPosition(this.offsetScroll.current, endBpSelection))
        }

        let newBrushSize = {
            x: startBpSelection * this.fontBoxWidth,
            width: startBpSelection === endBpSelection ? .1 : (endBpSelection - startBpSelection) * this.fontBoxWidth
        }

        if (this.rangeBp.start > this.rangeBp.end) {
            this.rangeBp.start = 0;
            newBrushSize.x = 0;
            newBrushSize.width = (this.rangeBp.end - this.rangeBp.start) * this.fontBoxWidth;
        }
        if (this.rangeBp.end > this.sequenceLength) {
            this.rangeBp.end = this.sequenceLength;
            newBrushSize.width = (this.sequenceLength - this.rangeBp.start) * this.fontBoxWidth;
        }

        this.brush.setParameters(newBrushSize)
        this.brush.selected();

        this.EventsTrigger("updateBrush", newBrushSize)
        this.EventsTrigger("selectRange", this.rangeBp);
        this.EventsTrigger("currentID", this.settings.id);
    }
    ,

    search(data) {
        this.resetSearch();

        this.searchResult.data = searchInAlignment(this.sequence, data.txtToSearch, this.slots, this.settings.id, this.sequenceLength)
        this.searchResult.textLength = data.txtToSearch.length

        this.EventsTrigger("resultSearch", {
            "result": this.searchResult.data,
            "src": this.settings.id
        })
    }
    ,

    resetSearch() {
        this.searchResult.data = [];
        this.searchResult.textLength = 0;

        this.mainBox.selectAll(".matchSearch").remove();
    }
    ,

    moveOnSearchResult(data) {
        this.rangeBp = data.rangeBp;

        const newBrushSize = {
            x: (this.rangeBp.start - this.offsetScroll.current) * this.fontBoxWidth,
            width: (this.rangeBp.end - this.rangeBp.start) * this.fontBoxWidth,
        }

        this.brush.setParameters(newBrushSize)

        this.drawResultMatchSearches()

        if (data.id !== this.settings.id) {
            this.mainBox.selectAll(".matchSearch").remove();
            this.brush.deselected();
        } else {
            this.brush.selected();
        }
    }
    ,

    selectRange(data) {
        this.rangeBp = {
            start: data.start,
            end: data.end
        };

        const newBrushSize = {
            x: (this.rangeBp.start - this.offsetScroll.current) * this.fontBoxWidth,
            width: (this.rangeBp.end - this.rangeBp.start) * this.fontBoxWidth,
        }

        this.brush.setParameters(newBrushSize);
    }
    ,

    copyBasePairsSelected(data) {
        if (data === this.settings.id) {
            const basePairsSelected = getBasePairs(this.sequence, this.slots, this.rangeBp.start, this.rangeBp.end)
            this.EventsTrigger("basePairsToCopy", basePairsSelected)
        }
    }
    ,

    toolTip() {
        const elements = document.querySelectorAll('[data-toggle="virtual-popper"]')
        renderVirtualPopper(elements);
    }
    ,
}

const brushMixins = {
    createBrush() {
        this.brush.setData(this.mainBox, this.initPosition, this.$mainBoxContainer)
        const [brush, brushFirstEdge, brushSecondEdge] = this.brush.create();

        [brushFirstEdge, brushSecondEdge].forEach(el => {
            el.call(d3.drag()
                .on("start drag end", (event) => this.eventOnDragBrushEdges(event, el))
            )
        })

        brush.call(d3.drag()
            .on("start drag end", (event) => this.eventOnDragBrush(event))
        )
    },

    /**
     * Brush Events
     */
    eventOnCreateBrush(event) {
        if (event.sourceEvent.detail < 2) {
            event.sourceEvent.preventDefault;

            this.brush.onCreateBrush(event)
            let brushSize = this.brush.getParameters();

            this.EventsTrigger("updateBrush", brushSize)

            if (event.type === "end") {

                this.alignRangeBp(brushSize);
                const selectedReferenceLength = (this.rangeBp.end - this.rangeBp.start);
                if (this.zoom === zoomLevels.max && selectedReferenceLength > 1) {

                    const basePairsContainer = Math.floor(this.$containerWidth / (this.regularSizeFont.width / zoomLevels.mid))
                    this.offsetScroll.current = Math.floor(this.rangeBp.start + (selectedReferenceLength / 2) - (basePairsContainer / 2));

                    this.setFontWidth(zoomLevels.mid)
                    this.slide("end")
                    this.selectRange(this.rangeBp)

                    this.EventsTrigger("setFontWidth", zoomLevels.mid)
                    this.EventsTrigger("setOffsetScroll", {current: this.offsetScroll.current})

                    this.EventsTrigger("slide", "end")
                }
            }
        }
    },

    eventOnDragBrush(event) {
        if (event.sourceEvent.detail < 2) {
            this.brush.updateOnDrag(event)

            let brushSize = this.brush.getParameters();
            this.EventsTrigger("updateBrush", brushSize)

            if (event.type === "end") {
                this.alignRangeBp(brushSize);
            } else if (event.type === "drag") {

                if (event.x < 0 || event.x > this.$mainBoxContainer.width) {
                    const scroll = event.x < 0 ? 1 : -1;

                    this.setScroll(scroll);
                    this.EventsTrigger("setScroll", scroll);
                    this.slide("end");
                    this.EventsTrigger("slide", "end");
                }

            }
        } else {
            /**
             * on doubleClick
             * */
            if (event.type === "end") {
                let brushSize = this.brush.getParameters();
                if (!this.brush.getOffsetDragX(event)) {
                    brushSize = {
                        x: event.x,
                        width: .1
                    }
                }
                this.alignRangeBp(brushSize);
            }
        }
    },

    eventOnDragBrushEdges(event, selector) {
        let scroll = 0;
        if (event.x < 0 || event.x > this.$mainBoxContainer.width) {
            scroll = event.x < 0 ? 1 : -1;
        }
        this.brush.updateOnDragEdges(event, selector, scroll * this.fontBoxWidth)
        let brushSize = this.brush.getParameters();
        this.EventsTrigger("updateBrush", brushSize)

        if (event.type === "end") {
            this.alignRangeBp(brushSize);
        } else if (event.type === "drag") {
            if (event.x < 0 || event.x > this.$mainBoxContainer.width) {

                this.setScroll(scroll);
                this.EventsTrigger("setScroll", scroll);
                this.slide("end");
                this.EventsTrigger("slide", "end");
            }
        }
    },

    updateBrush(data) {
        const startSelection = Math.round(data.x / this.fontBoxWidth);
        const endSelection = startSelection + Math.round(data.width / this.fontBoxWidth);

        this.rangeBp = {
            start: Math.abs(alignBpPosition(this.offsetScroll.current, startSelection)),
            end: Math.abs(alignBpPosition(this.offsetScroll.current, endSelection))
        }

        this.brush.setParameters(data);
        this.brush.deselected();
    },

    hideBrush() {
        this.brush.hide();
    },

    showBrush() {
        this.brush.show();
    },

    enableTooltip() {
        const elements = document.querySelectorAll('[data-toggle="virtual-popper"]')
        renderVirtualPopper(elements)
    }
}

const codingSequenceMixins = {
    drawStretchedCodingSequence(container, a, layer, annotationHeight) {
        const arrowLength = .4;
        const attributeAnnotation = attributeAnnotations(a.role_id, this.settings.annotation_colors);
        const codonContainers = [];
        const name = a.name ? a.name : "Unknown " + attributeAnnotation.name;

        if (this.threshold !== thresholdLevels.full) {
            a.steched_amino_sequence_positions.forEach((as, index) => {
                if (isInsideArea(as[0], as[0] + as[1], this.edges.left, this.edges.right, this.sequenceLength)) {

                    const pos = a.direction === 2 ? (a.amino_sequence.length - index) : index + 1;

                    let codonStart = 0;
                    let codonEnd = codonStart + as[1];
                    let codon = a.steched_amino_sequence[index];
                    const label = nomenclatures[codon] + " " + (pos) + " (" + name + ")";

                    const codonContainer = container
                        .append("g")
                        .attr("transform", "translate(" + (as[0] * this.fontBoxWidth) + ", " + layer + ")")
                        .attr("class", "annotationGroup annotation annotation_" + a.pk)
                        .attr('data-toggle', 'virtual-popper')
                        .attr("data-virtual-popper-content", label)
                        .datum({codon: index, codonLabel: codon, start: as[0], end: as[0] + as[1]})

                    codonContainer.append("path")
                        .attr("id", "pathAnnotationID_" + a.pk + "_" + index)
                        .attr("d", codonPath(0, annotationHeight, codonStart, codonEnd, a.direction, arrowLength, this.fontBoxWidth, false))
                        .style("fill", aminoSequenceColors[codon])
                        .style("stroke", aminoSequenceColors[codon])
                        .style("stroke-width", .5)
                        .style("opacity", .5)

                    if (annotationHeight > this.fontHeight) {
                        codonContainer
                            .append("text")
                            .attr("class", "annotation annotation_" + a.pk)
                            .attr("dy", (annotationHeight + this.fontHeight) / 2)
                            .append("textPath")
                            .attr("xlink:href", "#pathAnnotationID_" + a.pk + "_" + index)
                            .attr("startOffset", "17%")
                            .attr("class", "wrap_text")
                            .style("text-anchor", "middle")
                            .text(codon)
                    }
                    codonContainers.push(codonContainer)
                }
            })
        } else {
            const attributeAnnotation = attributeAnnotations(a.role_id, this.settings.annotation_colors);
            const label = a.name ? a.name : "Unknown " + attributeAnnotation.name;

            const annotationContainer = this.annotationsContainer
                .append("g")
                .attr("transform", "translate(" + (a.start * this.fontBoxWidth) + ", " + layer + ")")
                .attr("class", "annotationGroup annotation annotation_" + a.pk)
                .attr('data-toggle', 'virtual-popper')
                .attr("data-virtual-popper-content", label);

            if (!a.visible) {
                annotationContainer.attr("data-popper-reference-hidden", "");
            }

            this.drawPlainAnnotation(annotationContainer, a, attributeAnnotation.color, label, annotationHeight, annotationHeight * a.level)
        }

        return codonContainers;
    },
}

const annotationEventsMixins = {
    addAnnotationEvents(selector, annotation, start, end) {
        selector
            .on("click", e => {
                e.preventDefault();
                e.stopPropagation();
                const rangeBp = {
                    "start": start,
                    "end": rangeOverlapStart(start, end, this.sequenceLength)
                }

                this.selectRange(rangeBp);
                this.EventsTrigger("selectRange", this.rangeBp);
            })
            .on("contextmenu", e => {
                e.preventDefault();
                e.stopPropagation();
            })
    },

    addCodingSequenceEvents(selectors, annotation) {
        selectors.forEach(selector => {
            selector
                .on("contextmenu", e => {
                    e.preventDefault();
                    e.stopPropagation();
                })
                .on("click", (e, d) => {

                    let start, end;
                    if (!d) {
                        start = annotation.start;
                        end = annotation.end;
                    } else {
                        start = d.start;
                        end = d.end;
                    }

                    this.rangeBp = {
                        start: start,
                        end: rangeOverlapStart(start, end, this.sequenceLength)
                    }

                    const newBrushSize = this.setBrushSize();
                    this.alignRangeBp(newBrushSize, e);
                })
        })
    },
}

export {
    viewMixins,
    brushMixins,

    codingSequenceMixins,
    annotationEventsMixins,
};